// Scientific Agent Types

export interface StartSessionRequest {
  initialPrompt?: string;
  sessionType?: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
}

export interface SessionStartResponse {
  success: boolean;
  errors?: string[];
  sessionId?: string;
  visualization?: AgentVisualization;
}

export interface NicheAgentIterationRequest {
  agentType?: string;
  detailDomain?: string;
  customInstructions?: string;
  model?: string;
  temperature?: number;
  maxTokens?: number;
}

export interface NicheAgentIterationResponse {
  success: boolean;
  errors?: string[];
  visualization?: AgentVisualization;
}

export interface AgentVisualization {
  title: string;
  domain: string;
  detailDomain: string;
  description: string;
  keyTerms: string[];
  content: VisualizationContent;
  timestamp: string;
}

export interface VisualizationContent {
  overview: string;
  detailedExplanation: string;
  keyInsights: string[];
  implications?: string[];
  futureDirections?: string[];
  technicalDetails?: TechnicalDetail[];
  visualElements?: VisualElement[];
  references?: string[];
}

export interface TechnicalDetail {
  aspect: string;
  explanation: string;
  importance?: string;
}

export interface VisualElement {
  type: string;
  description: string;
  significance?: string;
}

// Session management
export interface ScientificSession {
  id: string;
  sessionId: string;
  agentType: ScientificAgentType;
  initialPrompt: string;
  visualizations: AgentVisualization[];
  createdAt: string;
  updatedAt: string;
}

export type ScientificAgentType = 
  | 'aerospace'
  | 'archaeological'
  | 'architecture'
  | 'astronomical'
  | 'botanical'
  | 'cell-biology'
  | 'climate'
  | 'ecology'
  | 'economics'
  | 'environment'
  | 'evolution'
  | 'extinction'
  | 'forensic'
  | 'genetic'
  | 'genetics'
  | 'geological'
  | 'linguistics'
  | 'marine'
  | 'materials'
  | 'mechanical'
  | 'medical'
  | 'microbiology'
  | 'neurological'
  | 'philosophy'
  | 'physical-chemistry'
  | 'political'
  | 'psychology'
  | 'robotics'
  | 'sociology';

export interface ScientificAgentInfo {
  id: ScientificAgentType;
  name: string;
  description: string;
  icon: string;
  category: 'engineering' | 'life-sciences' | 'earth-sciences' | 'social-sciences' | 'physical-sciences';
  domains: string[];
}

export const SCIENTIFIC_AGENTS: ScientificAgentInfo[] = [
  // Engineering
  {
    id: 'aerospace',
    name: 'Aerospace Engineering',
    description: 'Explore aircraft, spacecraft, and aerodynamic systems',
    icon: '🚀',
    category: 'engineering',
    domains: ['Aerodynamics', 'Propulsion', 'Structures', 'Control Systems', 'Orbital Mechanics']
  },
  {
    id: 'mechanical',
    name: 'Mechanical Engineering',
    description: 'Analyze mechanical systems, thermodynamics, and materials',
    icon: '⚙️',
    category: 'engineering',
    domains: ['Mechanics', 'Thermodynamics', 'Materials', 'Design', 'Manufacturing']
  },
  {
    id: 'robotics',
    name: 'Robotics Engineering',
    description: 'Design and analyze robotic systems and automation',
    icon: '🤖',
    category: 'engineering',
    domains: ['Kinematics', 'Control', 'Sensors', 'AI Integration', 'Actuators']
  },
  {
    id: 'materials',
    name: 'Materials Science',
    description: 'Study material properties, processing, and applications',
    icon: '🔬',
    category: 'engineering',
    domains: ['Metals', 'Polymers', 'Ceramics', 'Composites', 'Nanomaterials']
  },
  {
    id: 'architecture',
    name: 'Architecture & Design',
    description: 'Explore architectural principles and structural design',
    icon: '🏛️',
    category: 'engineering',
    domains: ['Design Theory', 'Structural Engineering', 'Sustainability', 'Urban Planning', 'History']
  },
  
  // Life Sciences
  {
    id: 'botanical',
    name: 'Botanical Science',
    description: 'Study plant biology, ecology, and applications',
    icon: '🌿',
    category: 'life-sciences',
    domains: ['Plant Biology', 'Ecology', 'Taxonomy', 'Physiology', 'Evolution']
  },
  {
    id: 'cell-biology',
    name: 'Cell Biology',
    description: 'Explore cellular structures, functions, and processes',
    icon: '🦠',
    category: 'life-sciences',
    domains: ['Cell Structure', 'Metabolism', 'Signaling', 'Division', 'Organelles']
  },
  {
    id: 'genetic',
    name: 'Genetic Science',
    description: 'Analyze genes, heredity, and genetic engineering',
    icon: '🧬',
    category: 'life-sciences',
    domains: ['DNA/RNA', 'Heredity', 'Mutations', 'Gene Expression', 'Genomics']
  },
  {
    id: 'genetics',
    name: 'Advanced Genetics',
    description: 'Deep dive into genetic research and applications',
    icon: '🔬',
    category: 'life-sciences',
    domains: ['CRISPR', 'Epigenetics', 'Population Genetics', 'Bioinformatics', 'Gene Therapy']
  },
  {
    id: 'medical',
    name: 'Medical Science',
    description: 'Explore human health, diseases, and treatments',
    icon: '⚕️',
    category: 'life-sciences',
    domains: ['Anatomy', 'Pathology', 'Pharmacology', 'Diagnostics', 'Treatment']
  },
  {
    id: 'microbiology',
    name: 'Microbiology',
    description: 'Study microorganisms and their interactions',
    icon: '🦠',
    category: 'life-sciences',
    domains: ['Bacteria', 'Viruses', 'Fungi', 'Parasites', 'Immunology']
  },
  {
    id: 'neurological',
    name: 'Neurological Science',
    description: 'Understand the brain and nervous system',
    icon: '🧠',
    category: 'life-sciences',
    domains: ['Brain Structure', 'Neural Networks', 'Cognition', 'Disorders', 'Neuroplasticity']
  },
  {
    id: 'evolution',
    name: 'Evolutionary Biology',
    description: 'Study the evolution of life and species',
    icon: '🦖',
    category: 'life-sciences',
    domains: ['Natural Selection', 'Speciation', 'Phylogeny', 'Adaptation', 'Fossil Record']
  },
  {
    id: 'extinction',
    name: 'Extinction Studies',
    description: 'Analyze extinction events and conservation',
    icon: '🦴',
    category: 'life-sciences',
    domains: ['Mass Extinctions', 'Conservation', 'Biodiversity', 'Climate Impact', 'Species Loss']
  },
  
  // Earth Sciences
  {
    id: 'geological',
    name: 'Geological Science',
    description: 'Study Earth\'s structure, processes, and history',
    icon: '🪨',
    category: 'earth-sciences',
    domains: ['Mineralogy', 'Petrology', 'Tectonics', 'Stratigraphy', 'Geomorphology']
  },
  {
    id: 'climate',
    name: 'Climate Science',
    description: 'Analyze climate systems and change',
    icon: '🌡️',
    category: 'earth-sciences',
    domains: ['Atmospheric Science', 'Ocean Currents', 'Climate Models', 'Global Warming', 'Weather']
  },
  {
    id: 'ecology',
    name: 'Ecological Science',
    description: 'Study ecosystems and environmental interactions',
    icon: '🌳',
    category: 'earth-sciences',
    domains: ['Ecosystems', 'Food Webs', 'Biodiversity', 'Conservation', 'Habitat']
  },
  {
    id: 'environment',
    name: 'Environmental Science',
    description: 'Analyze environmental systems and human impact',
    icon: '🌍',
    category: 'earth-sciences',
    domains: ['Pollution', 'Sustainability', 'Resource Management', 'Environmental Policy', 'Restoration']
  },
  {
    id: 'marine',
    name: 'Marine Science',
    description: 'Explore ocean systems and marine life',
    icon: '🌊',
    category: 'earth-sciences',
    domains: ['Oceanography', 'Marine Biology', 'Coral Reefs', 'Ocean Chemistry', 'Marine Ecosystems']
  },
  {
    id: 'astronomical',
    name: 'Astronomical Science',
    description: 'Study celestial objects and the universe',
    icon: '🌌',
    category: 'earth-sciences',
    domains: ['Planets', 'Stars', 'Galaxies', 'Cosmology', 'Astrobiology']
  },
  {
    id: 'archaeological',
    name: 'Archaeological Science',
    description: 'Explore human history through material remains',
    icon: '🏺',
    category: 'earth-sciences',
    domains: ['Excavation', 'Dating Methods', 'Cultural Analysis', 'Artifacts', 'Site Preservation']
  },
  
  // Physical Sciences
  {
    id: 'physical-chemistry',
    name: 'Physical Chemistry',
    description: 'Study chemical phenomena through physics',
    icon: '⚗️',
    category: 'physical-sciences',
    domains: ['Thermodynamics', 'Kinetics', 'Quantum Chemistry', 'Spectroscopy', 'Electrochemistry']
  },
  {
    id: 'forensic',
    name: 'Forensic Science',
    description: 'Apply science to legal investigations',
    icon: '🔍',
    category: 'physical-sciences',
    domains: ['Evidence Analysis', 'DNA Testing', 'Toxicology', 'Ballistics', 'Digital Forensics']
  },
  
  // Social Sciences
  {
    id: 'psychology',
    name: 'Psychology',
    description: 'Study human behavior and mental processes',
    icon: '🧠',
    category: 'social-sciences',
    domains: ['Cognitive', 'Behavioral', 'Developmental', 'Social', 'Clinical']
  },
  {
    id: 'sociology',
    name: 'Sociology',
    description: 'Analyze society and social behavior',
    icon: '👥',
    category: 'social-sciences',
    domains: ['Social Structure', 'Culture', 'Institutions', 'Social Change', 'Demographics']
  },
  {
    id: 'economics',
    name: 'Economics',
    description: 'Study economic systems and behavior',
    icon: '📊',
    category: 'social-sciences',
    domains: ['Microeconomics', 'Macroeconomics', 'Finance', 'Markets', 'Economic Policy']
  },
  {
    id: 'political',
    name: 'Political Science',
    description: 'Analyze political systems and governance',
    icon: '🏛️',
    category: 'social-sciences',
    domains: ['Government', 'Policy', 'International Relations', 'Political Theory', 'Elections']
  },
  {
    id: 'linguistics',
    name: 'Linguistics',
    description: 'Study language structure and evolution',
    icon: '💬',
    category: 'social-sciences',
    domains: ['Phonetics', 'Syntax', 'Semantics', 'Language Evolution', 'Sociolinguistics']
  },
  {
    id: 'philosophy',
    name: 'Philosophy',
    description: 'Explore fundamental questions of existence',
    icon: '🤔',
    category: 'social-sciences',
    domains: ['Ethics', 'Metaphysics', 'Epistemology', 'Logic', 'Philosophy of Science']
  }
];